import {
    asProfileUpdate,
    createProfileSchema,
    emptyProfileSchema,
  } from '@meprism/shared/src/schemas/EmployeeProfileForm'
import {
    useForm,
    UseFormProps,
    SubmitHandler,
    useFieldArray,
  } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Logger } from '@meprism/app-utils'
import { z } from 'zod'
import { UpdateExecutiveEmployeeProfile} from '@meprism/shared/src/redux/profile/ProfileSlice'
import { useAppDispatch } from '../../redux/storeExports'
  
const profileSchema = createProfileSchema(false, false, false, false)
  
  export type ProfileSchemaType = z.TypeOf<typeof profileSchema>
  
  export type ProfileFormHookReturn = ReturnType<
    typeof useForm<ProfileSchemaType>
  >
  
  export interface UseProfileFormProps {
    form?: UseFormProps<ProfileSchemaType>
    onSubmitSuccess?: () => void
    onSubmitError?: (error: any) => void
    middleNameOptional?: boolean
    linkedinOptional?: boolean
    streetOptional?: boolean
    agreeToTermsOptional?: boolean | undefined,
    muid: string,
    handleClose?: () => void
  }
  
  export const useProfileForm = (props: UseProfileFormProps) => {
    const dispatch = useAppDispatch()
    const submitHandler: SubmitHandler<ProfileSchemaType> = async (values) => {
      try {
        const muid = props.muid
        const payload = asProfileUpdate(values)
        props.handleClose?.()
        await dispatch(UpdateExecutiveEmployeeProfile({payload, muid}))
        if (props.onSubmitSuccess) {
          props.onSubmitSuccess()
        }
      } catch (error) {
        Logger.error(`Error submitting profile update: ${error}`)
        if (props.onSubmitError) {
          props.onSubmitError(error)
        }
      }
    }
  
    const {
      register,
      setValue,
      watch,
      setFocus,
      formState: { errors },
      control,
      handleSubmit,
      trigger,
      reset
    } = useForm<ProfileSchemaType>({
      resolver: zodResolver(
        createProfileSchema(
          Boolean(props.middleNameOptional),
          Boolean(props.linkedinOptional),
          Boolean(props.streetOptional),
          Boolean(props.agreeToTermsOptional),
        ),
      ),
      defaultValues: emptyProfileSchema(),
      mode: 'onChange'
    })
  
    const firstNameArray = useFieldArray({
      control,
      name: 'firstName' as const,
    })
  
    const otherFirstNameArray = useFieldArray({
      control,
      name: 'otherFirstName' as const,
    })
  
    const otherMaidenNameArray = useFieldArray({
      control,
      name: 'otherMaidenName' as const,
    })
  
    const middleNameArray = useFieldArray({
      control,
      name: 'middleName' as const,
    })
  
    const lastNameArray = useFieldArray({
      control,
      name: 'lastName' as const,
    })
  
    const addressArray = useFieldArray({
      control,
      name: 'address' as const,
    })
  
    const emailArray = useFieldArray({
      control,
      name: 'email' as const,
    })
  
    const phoneNumberArray = useFieldArray({
      control,
      name: 'phoneNumber' as const,
    })
  
    return {
      register,
      setValue,
      watch,
      errors,
      setFocus,
      control,
      handleSubmit,
      submitHandler,
      firstNameArray,
      middleNameArray,
      lastNameArray,
      otherFirstNameArray,
      otherMaidenNameArray,
      addressArray,
      emailArray,
      phoneNumberArray,
      trigger,
      reset
    }
  }